/**
 * Pipelines feature types.
 *
 * Re-exports the canonical domain types from `@ludus/core` so feature components don't
 * have to know whether a type comes from the engine or is a UI extension.
 *
 * The `LeadView` type is the presentation projection of the wire `Lead`
 * consumed across the feature. `data/usePipelinesSource.ts:enrichLead` derives
 * it from the engine's canonical `Lead` (v2 schema), flattening timestamps to
 * numbers and layering in derived fields (`contactName`, `value`, `lastTouchAt`,
 * …) that the dashboard / table read directly. A future contact join surfaces
 * real `company` values; today it falls back to empty.
 *
 * The `FunnelChannel` type is a UI mock for what will eventually be the new "Funnel"
 * (marketing channel / lead source) entity. Today it just labels `lead.funnel` for the
 * SidePanel breakdown; later it migrates to a real domain type with its own table.
 */

export type {
  Pipeline,
  PipelineId,
  InternalStatus,
  StatusId,
  Lead,
  LeadId,
  Manager,
  ManagerId,
  CrmId,
  LifecyclePhase,
  EngineRole,
} from '@ludus/core'

// Value re-exports (the closed enum option lists) for the settings picker.
export { LIFECYCLE_PHASES, ENGINE_ROLES } from '@ludus/core'

import type { LeadId, ManagerId, PipelineId, StatusId } from '@ludus/core'

export interface LeadView {
  id: LeadId
  externalRef: string
  pipelineId: PipelineId
  statusId: StatusId
  assignedManagerId: ManagerId | null
  funnelKey: string | null
  contactName: string
  company: string
  /** EUR, whole units. */
  value: number
  /** 0–100. */
  probability: number
  tags: string[]
  nextAction: string | null
  lastTouchAt: number | null
  createdAt: number
  assignedAt: number | null
}

// Re-exported from shared so the historical `from '@/features/pipelines/types'` import
// path keeps working for in-feature consumers; new code reaches `@/shared/utils/funnels`
// directly.
export type { FunnelChannel } from '@/shared/utils/funnels.ts'

/**
 * Static per-pipeline config the page gates on. Lead data is NOT here — as of
 * SBO-48/52 leads are fetched server-paginated/filtered via `useStageLeads`
 * and aggregated via `useLeadStats`, so the full array no longer lives client-side.
 */
export interface PipelineSnapshot {
  pipeline: import('@ludus/core').Pipeline
  stages: import('@ludus/core').InternalStatus[]
  /**
   * Optional lifecycle classification per stage id (intake / cool / mid / warm / won / lost).
   * Drives the stage palette in the dashboard and lets terminal lost states render with
   * their own colour. Adapters that don't surface this map yet leave it empty — the UI
   * falls back to position-based bucketing.
   */
  stageKinds: Record<string, import('@/shared/utils/stageColors.ts').StageKind>
  /** Every manager of the tenant — who a card may be owned by. */
  managers: import('@ludus/core').Manager[]
  /** The pipeline's participants — who a new card may be handed to. */
  participants: import('@ludus/core').Manager[]
}

export interface StageMetrics {
  stageId: StatusId
  leadCount: number
  totalValue: number
  /** Median age in days of leads currently in the stage. */
  medianAgeDays: number
  /** Count of leads flagged as "stuck" (age > stage threshold). */
  stuckCount: number
  /** % conversion forward to next stage in pipeline. `null` for the terminal stage. */
  toNext: number | null
  /** % conversion received from previous stage. `null` for the first stage. */
  fromPrev: number | null
}

export type SortKey = 'age' | 'value' | 'lastAction'
export type SortDirection = 'asc' | 'desc'

export interface SortState {
  key: SortKey
  direction: SortDirection
}

export type PeriodPreset = 'today' | '7d' | '30d' | 'all'

export interface FilterDraft {
  managerIds: ManagerId[]
  period: PeriodPreset
  search: string
}

// Default period is a bounded recent window, not `all` (SBO-48/52): it caps the
// common-case server scan + offset depth to ~one month of leads. Wider windows
// (`all`, custom) are opt-in and heavier but acceptable at the stated scale.
export const EMPTY_FILTER_DRAFT: FilterDraft = {
  managerIds: [],
  period: '30d',
  search: '',
}

export type AgingBucket = '0-3d' | '4-7d' | '8-14d' | '15-30d' | '30d+'

export const AGING_BUCKETS: readonly AgingBucket[] = [
  '0-3d',
  '4-7d',
  '8-14d',
  '15-30d',
  '30d+',
] as const
